/** 水印画布对象，连接水印位图生成与无界画布的绘制、视口裁剪和导出 */
import { EdgelessObject, normalizeResultScale, type IFramework, type IResultOptions, type TBBox } from '@/framework';
import { LayerType, type IWatermarkLayerProps } from '@/version-types';
import { renderLayerContentToCanvas } from './render/render-to-canvas';

export class EdgelessWatermarkLayer extends EdgelessObject {
    static type = LayerType.WATERMARK;

    constructor(layerId: string, framework: IFramework) {
        super(layerId, framework.service);
    }

    private getLayer() {
        return this.service.flatLayer.getLayer(this.layerId);
    }

    private async getResource(scale?: number) {
        const layer = this.getLayer();
        const retinaScaling = this.service.edgelessCanvas.retinaScaling;
        const resultScale =
            scale ??
            super.getResourceScale({
                naturalWidth: layer.width * retinaScaling,
                naturalHeight: layer.height * retinaScaling,
            });
        return renderLayerContentToCanvas(layer, resultScale);
    }

    override supportsCanvasResult() {
        return true;
    }

    override async getResult({ aiWatermark = false, scale = 1 }: IResultOptions = {}) {
        const renderBox = this.getBaseInfo();
        const canvas = await this.getResource(normalizeResultScale(scale));
        if (aiWatermark) {
            await super.drawAiWatermarkAsync(canvas);
        }
        return { canvas, renderBox };
    }

    override async loadResource() {
        const canvas = await this.getResource();
        await super.drawAiWatermarkAsync(canvas);
        return canvas;
    }

    /** 同步可见性判断与绘制使用的角度，平铺图案的旋转已包含在位图中 */
    override setCoords() {
        const layer = this.getLayer();
        this.angle = (layer.data as IWatermarkLayerProps).isFullScreen ? 0 : layer.rotate;
        super.setCoords();
    }

    override getBaseInfo(): TBBox {
        const layer = this.getLayer();
        const isFullScreen = (layer.data as IWatermarkLayerProps).isFullScreen;
        const position = this.calcCenterIsLeftTopPosition({
            left: layer.left,
            top: layer.top,
            rotate: isFullScreen ? 0 : layer.rotate,
            width: layer.width,
            height: layer.height,
        });

        return {
            ...position,
            width: layer.width,
            height: layer.height,
        };
    }
}
